import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { EnterButton } from '@backrs_ui/grow-ui/src/components/Lubav/EnterButton';
import { RankChips } from '@backrs_ui/grow-ui/src/components/MyColleges/RankChips';
import { StepSlider } from '@backrs_ui/grow-ui/src/components/MyColleges/StepSlider';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { SkipChatButton } from './SkipChatButton';

type Slider = Readonly<{ label: string; defaultValue?: string }>;

type StepSliderChatQuestionProps = Readonly<{
  idPrefix: string;
  steps: readonly string[];
  sliders: readonly Slider[];
  ranking?: readonly string[];
  required: boolean;
  isTyping: boolean;
  onSubmit: (answer: string) => void;
  onSkip: () => void;
}>;

export const StepSliderChatQuestion = ({
  idPrefix,
  steps,
  sliders,
  ranking,
  required,
  isTyping,
  onSubmit,
  onSkip,
}: StepSliderChatQuestionProps) => {
  const [values, setValues] = useState(() =>
    sliders.map((slider) => Math.max(steps.indexOf(slider.defaultValue ?? ''), 0)),
  );
  const [order, setOrder] = useState(ranking ?? []);

  const answer = () => {
    const levels = sliders.map((slider, index) => `${slider.label}: ${steps[values[index]]}`);

    return [ranking && order.join(' > '), levels.join(', ')].filter(Boolean).join('; ');
  };

  return (
    <Box sx={containerSx}>
      {ranking && (
        <RankChips
          id={`${idPrefix}-rank`}
          label={sliders[0]?.label ?? ''}
          order={order}
          optionLabel={(option) => option}
          onReorder={setOrder}
        />
      )}
      {sliders.map((slider, index) => (
        <Box key={slider.label} sx={sliderSx}>
          <Typography variant="text-sm-medium" sx={labelSx}>
            {slider.label}
          </Typography>
          <StepSlider
            id={`${idPrefix}-slider-${index}`}
            label={slider.label}
            steps={steps}
            value={values[index]}
            onChange={(value) => setValues(values.map((old, i) => (i === index ? value : old)))}
          />
        </Box>
      ))}
      <Box sx={actionsRowSx}>
        <EnterButton
          id={`${idPrefix}-submit`}
          onClick={() => onSubmit(answer())}
          label={appStrings.modules.solutionSessions.questions.submit}
          showEnterHint
          disabled={isTyping}
        />
        {required ? (
          <Box />
        ) : (
          <SkipChatButton id={`${idPrefix}-skip`} disabled={isTyping} onSkip={onSkip} />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const sliderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const actionsRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
};
